import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, PhoneCall, Camera, Loader2, Zap } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
    {
        name: "Starter Savv",
        desc: "For businesses ready to clean up their visual presence.",
        includes: "Brand essentials + design support",
        cta: "Get Started",
        lookup: "starter_savv",
        list: "$621",
        price: "$497",
        per: "one-time",
        badge: "bg-[#C9DDEE] text-[#4E7499]",
        featured: false,
    },
    {
        name: "Social Savv",
        desc: "For businesses ready to look consistent online.",
        includes: "Social graphics + content visuals + branded templates",
        cta: "Get Started",
        lookup: "social_savv",
        list: "$746",
        price: "$597",
        per: "per month",
        badge: "bg-[#EAF1F7] text-[#4E7499]",
        featured: false,
    },
    {
        name: "Brand Savv",
        desc: "For businesses ready for a complete identity.",
        includes: "Branding + visual identity + marketing materials",
        cta: "Get Started",
        lookup: "brand_savv",
        list: "$1,497",
        price: "$1,197",
        per: "one-time",
        badge: "bg-[#0C0C0C] text-[#C9DDEE]",
        featured: true,
    },
    {
        name: "Full Savv",
        desc: "For businesses wanting ongoing creative support.",
        includes: "Design + marketing + content + brand support",
        cta: "Let's Talk",
        lookup: "full_savv",
        list: "$2,497",
        price: "$1,997",
        per: "per month",
        badge: "bg-[#0C0C0C] text-[#F1F1EF]",
        featured: false,
    },
];

export const Packages = () => {
    const [paying, setPaying] = useState(null);

    const prepay = async (lookup, i) => {
        setPaying(i);
        try {
            const { data } = await axios.post(`${API}/payments/checkout`, {
                lookup_key: lookup,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch {
            toast.error("Couldn't start checkout — try again or book a call instead.");
            setPaying(null);
        }
    };

    return (
        <section
            id="packages"
            data-testid="packages-section"
            className="py-20 md:py-32 px-5 sm:px-8 lg:px-12 bg-[#101010] border border-[#262626] rounded-[2.5rem] mx-3 md:mx-6 my-6"
        >
            <div className="max-w-7xl mx-auto">
                <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
                    <Reveal>
                        <Eyebrow>Packages</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-4 font-serif-ed text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-[#F1F1EF]">
                            Find your{" "}
                            <span className="italic text-[#D3A0A7]">Savv.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-4 text-sm md:text-base text-[#A9A6A0]">
                            Founding rates for pre-paid clients — 20% off, plus
                            your strategy call and brand photo shoot on us.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 items-stretch">
                    {PACKAGES.map((p, i) => (
                        <Reveal key={p.name} delay={i * 0.08} className="h-full">
                            <motion.div
                                data-testid={`package-card-${i}`}
                                whileHover={{ y: -8 }}
                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                className={`relative h-full flex flex-col rounded-3xl p-8 border ${
                                    p.featured
                                        ? "bg-[#C9DDEE] border-[#C9DDEE] shadow-[0_30px_60px_-20px_rgba(201,221,238,0.4)]"
                                        : "bg-[#0C0C0C] border-[#262626]"
                                }`}
                            >
                                {p.featured && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 font-hand text-xl text-[#C9DDEE] bg-[#0C0C0C] px-4 py-0.5 rounded-full rotate-[-2deg]">
                                        most loved ♡
                                    </span>
                                )}
                                <span
                                    className={`self-start text-[11px] font-semibold tracking-[0.16em] uppercase px-3 py-1.5 rounded-full ${p.badge}`}
                                >
                                    {p.name}
                                </span>
                                <p
                                    className={`mt-6 text-[15px] font-semibold leading-snug ${
                                        p.featured ? "text-[#0C0C0C]" : "text-[#F1F1EF]"
                                    }`}
                                >
                                    {p.desc}
                                </p>
                                <p
                                    className={`mt-4 text-sm leading-relaxed ${
                                        p.featured ? "text-[#0C0C0C]/70" : "text-[#A9A6A0]"
                                    }`}
                                >
                                    {p.includes}
                                </p>
                                <div data-testid={`package-price-${i}`} className="mt-6">
                                    <span
                                        className={`font-serif-ed italic text-lg line-through ${
                                            p.featured ? "text-[#0C0C0C]/45" : "text-[#A9A6A0]/60"
                                        }`}
                                    >
                                        {p.list}
                                    </span>
                                    <span
                                        className={`ml-2 font-serif-ed text-3xl tracking-tight ${
                                            p.featured ? "text-[#0C0C0C]" : "text-[#F1F1EF]"
                                        }`}
                                    >
                                        {p.price}
                                    </span>
                                    <span
                                        className={`ml-2 font-sans text-xs font-medium tracking-wide ${
                                            p.featured ? "text-[#0C0C0C]/60" : "text-[#A9A6A0]"
                                        }`}
                                    >
                                        {p.per}
                                    </span>
                                </div>
                                <p
                                    data-testid={`package-perk-${i}`}
                                    className={`mt-2 font-hand text-lg leading-tight ${
                                        p.featured
                                            ? "text-[#0C0C0C]/70"
                                            : "text-[#A9C6DE]"
                                    }`}
                                >
                                    founding rate — 20% off + free call + brand shoot ♡
                                </p>
                                <button
                                    onClick={() => prepay(p.lookup, i)}
                                    disabled={paying !== null}
                                    data-testid={`package-prepay-${i}`}
                                    className="mt-6 w-full inline-flex items-center justify-center gap-2 font-semibold text-sm px-5 py-3 rounded-full transition-colors duration-300 disabled:opacity-60 bg-[#C9DDEE] text-[#0C0C0C] hover:bg-[#F1F1EF]"
                                >
                                    {paying === i ? (
                                        <>
                                            Opening checkout <Loader2 size={15} className="animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            <Zap size={15} /> Pre-pay at Founding Rate
                                        </>
                                    )}
                                </button>
                                <a
                                    href="#contact"
                                    data-testid={`package-cta-${i}`}
                                    className={`group mt-4 inline-flex items-center justify-center gap-2 text-sm font-semibold ${
                                        p.featured ? "text-[#0C0C0C]" : "text-[#F1F1EF]"
                                    }`}
                                >
                                    <span className="link-underline">{p.cta} instead</span>
                                    <ArrowRight
                                        size={15}
                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                    />
                                </a>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15}>
                    <div
                        data-testid="packages-prepay-banner"
                        className="mt-10 bg-[#262626] rounded-3xl px-8 py-8 md:px-12 md:py-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left"
                    >
                        <div className="flex flex-col sm:flex-row items-center gap-5">
                            <span className="flex -space-x-3">
                                <span className="w-12 h-12 rounded-full bg-[#C9DDEE] text-[#0C0C0C] flex items-center justify-center">
                                    <PhoneCall size={19} />
                                </span>
                                <span className="w-12 h-12 rounded-full bg-[#F1F1EF] text-[#0C0C0C] flex items-center justify-center">
                                    <Camera size={19} />
                                </span>
                            </span>
                            <p className="font-serif-ed text-2xl md:text-[1.7rem] leading-snug text-[#F1F1EF] max-w-xl">
                                Pre-pay as a founding client:{" "}
                                <span className="italic text-[#C9DDEE]">20% off</span>,{" "}
                                <span className="italic text-[#C9DDEE]">
                                    strategy call
                                </span>{" "}
                                +{" "}
                                <span className="italic text-[#C9DDEE]">
                                    brand photo shoot
                                </span>{" "}
                                — all on us.
                            </p>
                        </div>
                        <a
                            href="#contact"
                            data-testid="packages-prepay-cta"
                            className="group shrink-0 inline-flex items-center gap-2 bg-[#C9DDEE] text-[#0C0C0C] font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-[#F1F1EF] transition-colors duration-300"
                        >
                            Claim the Perk
                            <ArrowRight
                                size={16}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
